// @ts-strict-ignore
import { datadogRum } from '@datadog/browser-rum';
import { useMutation } from '@tanstack/react-query';
import {
  Box,
  Button,
  ButtonSize,
  ButtonVariant,
  Chip,
  Icon,
  IconName,
  TextField,
  Tooltip,
  useSnackBar,
} from '@usealloy/component-library';
import React, { useContext, useState } from 'react';
import { UserContext } from '~/dashboard/context';
import type { Document } from '~/entities/types';
import { ajaxFetch } from '~/helpers/api';
import apiCalls from '~/helpers/apiCalls';

interface Props {
  document: Document;
  hideReviewActions?: boolean;
  onReviewSuccess?: () => void;
  shouldPinReviewActions?: boolean;
}

export const DocReviewActions: React.FC<Props> = ({
  document,
  hideReviewActions = false,
  onReviewSuccess,
  shouldPinReviewActions = false,
}) => {
  const {
    typhonDomain, agent, permissions, featureFlags,
  } = useContext(UserContext);
  const addSnackbar = useSnackBar();
  const { notes: { createNote } } = apiCalls;
  const [selectedAction, setSelectedAction] = useState<'approve' | 'deny' | 'resubmit' | null>(null);
  const [reviewNote, setReviewNote] = useState<string>('');

  const docIsAlreadyReviewed = document.approval_outcome !== null;
  const showResubmitAction = featureFlags.documentReRequest;
  const isAlloyAdmin = agent.isEmployee;
  const canReviewDocs = !!permissions['UPDATE:Documents:approved'];

  const {
    mutateAsync: submitDocReview,
    isLoading: isDocReviewSubmitting,
  } = useMutation({
    mutationFn: async () => {
      if (!canReviewDocs) {
        throw new Error('You do not have permission to review this document');
      }

      if (!selectedAction) return;

      datadogRum.addAction('DocReviewV2Modal', {
        action: 'REVIEW_DOC',
        context: {
          docToken: document.document_token,
          reviewStatus: selectedAction,
          withNote: !!reviewNote,
        },
      });

      const approvedValue = selectedAction === 'resubmit' ? undefined : selectedAction === 'approve';
      const alternativeOutcomeValue = selectedAction === 'resubmit' ? 'resubmit' : undefined;

      await ajaxFetch(document.review_url, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          approved: approvedValue,
          alternative_outcome: alternativeOutcomeValue,
        }),
      });

      /* extra validation as part of migration process */
      if (!document.document_id || isNaN(document.document_id)) {
        throw new Error('Document ID is required and must be a number');
      }
      if (!document.document_token?.trim()) {
        throw new Error('Document token is required');
      }
      if (!document.entity_token?.trim()) {
        throw new Error('Entity token is required');
      }

      if (reviewNote.length > 0) {
        await createNote({
          typhonDomain,
          body: {
            text: reviewNote,
            table: 'Documents',
            key: 'document_id',
            row_id: document.document_id,
            token: document.document_token,
            entity_token: document.entity_token,
          },
        });
      }
    },
    onSettled: () => {
      setSelectedAction(null);
      setReviewNote('');
    },
    onError: (err: any) => {
      addSnackbar({ type: 'danger', title: 'Error Reviewing Document', body: err?.message });
    },
    onSuccess: () => {
      addSnackbar({ type: 'success', title: 'Document Reviewed' });
      onReviewSuccess?.();
    },
  });

  if (isAlloyAdmin || docIsAlreadyReviewed || hideReviewActions) return null;

  return (
    <Box
      gap={4}
      justifyContent="flex-start"
      style={{
        ...(shouldPinReviewActions ? {
          position: 'absolute',
          bottom: 0,
          left: 0,
          right: 0,
        } : {}),

        backgroundColor: 'var(--cl-background-default)',
        padding: 'var(--cl-space-5)',
        borderRadius: '0 0 var(--cl-radius-2) var(--cl-radius-2)',
        borderTop: '1px solid var(--cl-border-default)',
      }}
      data-pendo-anchor="document-review-review-actions"
    >
      <Chip
        disabled={docIsAlreadyReviewed || !canReviewDocs}
        type="radio"
        value="approve"
        selected={selectedAction === 'approve'}
        onChange={() => setSelectedAction('approve')}
        style={{
          margin: 0,
          paddingBlock: 'var(--cl-space-1)',
          paddingInline: 'var(--cl-space-2)',
          alignItems: 'center',
        }}
      >
        <Icon icon={IconName.Approved} />
        Approve
      </Chip>
      <Chip
        disabled={docIsAlreadyReviewed || !canReviewDocs}
        type="radio"
        value="deny"
        selected={selectedAction === 'deny'}
        onChange={() => setSelectedAction('deny')}
        style={{
          margin: 0,
          paddingBlock: 'var(--cl-space-1)',
          paddingInline: 'var(--cl-space-2)',
          alignItems: 'center',
        }}
      >
        <Icon icon={IconName.Xmark} />
        Deny
      </Chip>
      {showResubmitAction && (
        <Chip
          disabled={docIsAlreadyReviewed || !canReviewDocs}
          type="radio"
          value="resubmit"
          selected={selectedAction === 'resubmit'}
          onChange={() => setSelectedAction('resubmit')}
          style={{
            margin: 0,
            paddingBlock: 'var(--cl-space-1)',
            paddingInline: 'var(--cl-space-2)',
            alignItems: 'center',
          }}
        >
          <Icon icon={IconName.Retry} />
          Resubmit
        </Chip>
      )}
      <TextField
        disabled={docIsAlreadyReviewed || !canReviewDocs}
        type="text"
        placeholder="Add a note to your review"
        value={reviewNote}
        onChange={e => setReviewNote(e.target.value)}
      />
      <Tooltip
        disabled={canReviewDocs}
        content={!canReviewDocs ? 'You do not have permission to review this document' : ''}
        side="top"
      >
        <Button
          disabled={!selectedAction || docIsAlreadyReviewed || !canReviewDocs}
          loading={isDocReviewSubmitting}
          onClick={() => submitDocReview()}
          size={ButtonSize.medium}
          variant={ButtonVariant.primary}
        >
          Review
        </Button>
      </Tooltip>
    </Box>
  );
};
